import { sortByName, sortAsc } from '../utils/sortOptions';
import PasswordInput from '../components/PasswordInput';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, EyeIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { TYPE_LABELS as REQUEST_TYPE_LABELS, payloadSummary, fmtDateDMY } from '../requestDisplay';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${dt.toLocaleDateString('en-US',{month:'short'})}-${dt.getFullYear()}`;
};

const sharedStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cp * { box-sizing:border-box; }
  .cp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:cpFade .4s ease both; }
  @keyframes cpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .cp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .cp-title  { font-size:1.3rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .cp-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:14px; height:14px; stroke-width:2; }
  .btn-orange { background:#f97316; color:white; }
  .btn-orange:hover { background:#ea6c0a; box-shadow:0 4px 14px rgba(249,115,22,.3); }
  .btn-green  { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title { font-size:.82rem; font-weight:600; color:#1a1d2e; }
  .row-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .pill { font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pill-rejected { background:#fef2f2; color:#ef4444; }
  .pill-orange   { background:#fff7ed; color:#f97316; }
  .cp-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .cp-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal-box { background:white; border-radius:20px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:.92rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body { padding:18px 20px; }
  .modal-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 20px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.full { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-label .req { color:#ef4444; margin-left:2px; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#f97316; background:white; }
  .form-textarea { resize:vertical; min-height:80px; }
  .info-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:10px 14px; font-size:.78rem; color:#c2410c; margin-bottom:14px; }

  .cp-cards { display:none; padding:10px; }
  .cp-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .cp-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .cp-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .cp-card-row:last-of-type { border-bottom:none; }
  .cp-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }
  .cp-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }

  @media(max-width:768px){
    .form-grid{grid-template-columns:1fr;} .form-field.full{grid-column:1;} .modal-foot{justify-content:stretch;} .modal-foot .btn{flex:1;justify-content:center;}
    .cp-tbl-wrap { display:none !important; }
    .cp-cards { display:block; }
  }
`;

// ── Check-in card (same shape as Admin/Finance dashboards) ────────────────────
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : '—';
const checkinCardCss = `
  .checkin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-info  { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .checkin-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .checkin-dot.in  { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .checkin-dot.out { background:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,.2); }
  .checkin-dot.off { background:#d1d5db; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px rgba(16,201,142,.2)} 50%{box-shadow:0 0 0 6px rgba(16,201,142,.05)} }
  .checkin-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .checkin-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .checkin-times { display:flex; gap:1.5rem; flex-shrink:0; flex-wrap:wrap; }
  .checkin-time-item  { text-align:center; }
  .checkin-time-label { font-size:.65rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
  .checkin-time-val   { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#1a1d2e; margin-top:2px; }
  .checkin-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .checkin-btn svg { width:15px; height:15px; stroke-width:2; }
  .checkin-btn.green { background:#10c98e; color:white; }
  .checkin-btn.green:hover { background:#0db87e; box-shadow:0 4px 14px rgba(16,201,142,.35); transform:translateY(-1px); }
  .checkin-btn.red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .checkin-btn.red:hover   { background:#ef4444; color:white; }
  .checkin-btn.ghost { background:#f0f2f7; color:#9094a8; }
  .checkin-btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  @media (max-width:768px) { .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; } }
`;
const CheckinCard = ({ today, actioning, onCheckIn, onCheckOut }) => {
  if (!today?.checkinEnabled) return null;
  const rec    = today?.record;
  const hasIn  = !!rec?.checkIn;
  const hasOut = !!rec?.checkOut;
  return (
    <div className="checkin-card"><style>{checkinCardCss}</style>
      <div className="checkin-info">
        <div className={`checkin-dot ${hasOut?'out':hasIn?'in':'off'}`}/>
        <div>
          <div className="checkin-label">{hasOut?'Checked Out':hasIn?'Currently Checked In':'Not Checked In'}</div>
          <div className="checkin-sub">{rec?.lateMinutes>0?`${rec.lateMinutes}m late today`:'Right on time'}</div>
        </div>
      </div>
      {(hasIn||hasOut)&&(
        <div className="checkin-times">
          <div className="checkin-time-item"><div className="checkin-time-label">Check In</div><div className="checkin-time-val">{fmtTime(rec?.checkIn)}</div></div>
          {hasOut&&<div className="checkin-time-item"><div className="checkin-time-label">Check Out</div><div className="checkin-time-val">{fmtTime(rec?.checkOut)}</div></div>}
          {hasOut&&rec?.workHours&&<div className="checkin-time-item"><div className="checkin-time-label">Hours</div><div className="checkin-time-val">{Number(rec.workHours||0).toFixed(1)}h</div></div>}
        </div>
      )}
      <div style={{width:'100%',maxWidth:200}}>
        {!hasIn&&<button className="checkin-btn green" onClick={onCheckIn} disabled={actioning}><CheckCircleIcon/>{actioning?'Please wait…':'Check In'}</button>}
        {hasIn&&!hasOut&&<button className="checkin-btn red" onClick={onCheckOut} disabled={actioning}><XCircleIcon/>{actioning?'Please wait…':'Check Out'}</button>}
        {hasOut&&<button className="checkin-btn ghost" disabled>Done for today</button>}
      </div>
    </div>
  );
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export const CoordinatorDashboard = () => {
  const [stats, setStats] = useState({ students:0, teachers:0, classes:0, pendingRequests:0 });
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, t, c, r] = await Promise.allSettled([
          api.get('/coordinator/students'),
          api.get('/coordinator/teachers'),
          api.get('/coordinator/classes'),
          api.get('/coordinator/requests?status=pending'),
        ]);
        setStats({
          students:       s.status === 'fulfilled' ? (s.value.data?.total ?? s.value.data?.students?.length ?? 0) : 0,
          teachers:       t.status === 'fulfilled' ? (t.value.data?.total ?? t.value.data?.teachers?.length ?? 0) : 0,
          classes:        c.status === 'fulfilled' ? (c.value.data?.length || 0) : 0,
          pendingRequests:r.status === 'fulfilled' ? (r.value.data?.length || 0) : 0,
        });
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    fetchToday();
  }, []);

  const fetchToday = async () => {
    try { const { data: res } = await api.get('/coordinator/own-attendance/today'); setToday(res); } catch { /* silent */ }
  };
  const handleCheckIn = async () => {
    try { setActioning(true); await api.post('/coordinator/own-attendance/checkin'); toast.success('Checked in!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-in failed'); }
    finally { setActioning(false); }
  };
  const handleCheckOut = async () => {
    try { setActioning(true); await api.post('/coordinator/own-attendance/checkout'); toast.success('Checked out!'); fetchToday(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-out failed'); }
    finally { setActioning(false); }
  };

  const statItems = stats ? [
    { label:'Total Students', value:stats.students,       color:'#3b6ef8', bg:'#eef2ff', icon:'🎓' },
    { label:'Total Teachers', value:stats.teachers,       color:'#10c98e', bg:'#ecfdf5', icon:'👨‍🏫' },
    { label:'Classes',        value:stats.classes,        color:'#8b5cf6', bg:'#f3f0ff', icon:'🏫' },
    { label:'Pending Requests',value:stats.pendingRequests,color:'#f97316', bg:'#fff7ed', icon:'📋' },
  ] : [];

  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header">
        <div><div className="cp-title">Dashboard</div><div className="cp-sub">Welcome to the Coordinator Portal</div></div>
      </div>
      <CheckinCard today={today} actioning={actioning} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} />
      {loading ? <div className="cp-loading"><div className="spinner"/>Loading…</div> : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'1rem',marginBottom:'1.4rem'}}>
          {statItems.map(s => (
            <div key={s.label} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1rem 1.2rem'}}>
              <div style={{width:36,height:36,borderRadius:10,background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',marginBottom:8}}>{s.icon}</div>
              <div style={{fontSize:'.72rem',fontWeight:600,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>{s.label}</div>
              <div style={{fontSize:'1.6rem',fontWeight:700,color:s.color,fontFamily:"'JetBrains Mono',monospace",marginTop:4}}>{s.value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Quick Actions</div></div>
        <div style={{padding:'18px 20px',display:'flex',gap:10,flexWrap:'wrap'}}>
          <button className="btn btn-orange" onClick={()=>window.location.href='/coordinator/requests'}><PlusIcon />Submit Request</button>
          <button className="btn btn-ghost" onClick={()=>window.location.href='/coordinator/notices'}>View Notices</button>
        </div>
      </div>
    </div>
  );
};

// ─── VIEW LISTS (Students, Teachers, Classes) ─────────────────────────────────
const ViewList = ({ title, endpoint, columns }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get(endpoint).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [endpoint]);

  const filtered = data.filter(item =>
    columns.some(col => String(col.get(item) || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header"><div><div className="cp-title">{title}</div><div className="cp-sub">View only — submit a request to add new records</div></div></div>
      <div style={{marginBottom:'1rem'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`}
          style={{padding:'9px 14px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none',width:'100%',maxWidth:360}} />
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">{title} ({filtered.length})</div></div>
        {loading ? <div className="cp-loading"><div className="spinner"/>Loading…</div>
        : filtered.length === 0 ? <div className="cp-empty">No {title.toLowerCase()} found.</div>
        : filtered.map((item, i) => (
          <div key={item._id || i} className="row-item">
            {columns.map(col => (
              <div key={col.label} style={{flex:col.flex||1,minWidth:0}}>
                {col.primary ? <div className="row-title">{col.get(item)}</div>
                             : <div className="row-sub">{col.label}: {col.get(item)}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const CoordinatorStudents = () => <ViewList title="Students" endpoint="/coordinator/students" columns={[
  { label:'Name',       get: s => s.user?.name,        primary:true, flex:2 },
  { label:'Admission',  get: s => s.admissionNo },
  { label:'Class',      get: s => `${s.class?.name} - ${s.section}` },
  { label:'Roll',       get: s => s.rollNo },
]} />;

export const CoordinatorTeachers = () => <ViewList title="Teachers" endpoint="/coordinator/teachers" columns={[
  { label:'Name',        get: t => t.user?.name,    primary:true, flex:2 },
  { label:'Email',       get: t => t.user?.email },
  { label:'Employee ID', get: t => t.employeeId },
  { label:'Classes',     get: t => t.assignedClasses?.map(c=>c.name).join(', ') || '—' },
]} />;

export const CoordinatorClasses = () => <ViewList title="Classes" endpoint="/coordinator/classes" columns={[
  { label:'Class',    get: c => c.name,                           primary:true },
  { label:'Sections', get: c => c.sections?.join(', ') || '—' },
  { label:'Subjects', get: c => c.subjects?.slice(0,3).join(', ') || '—' },
]} />;

// ─── NOTICES ──────────────────────────────────────────────────────────────────
const COORDINATOR_NOTICE_AUDIENCES = [
  { value:'teacher',     label:'Teachers'     },
  { value:'coordinator', label:'Coordinators' },
  { value:'finance',     label:'Finance'      },
  { value:'student',     label:'Students'     },
  { value:'all',         label:'All'          },
];

export const CoordinatorNotices = () => {
  const [notices,    setNotices]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [open,       setOpen]       = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [teachers,   setTeachers]   = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [financeMembers, setFinanceMembers] = useState([]);
  const [students,   setStudents]   = useState([]);
  const [classes,    setClasses]    = useState([]);
  const [filterClass,   setFilterClass]   = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [form, setForm] = useState({ title:'', content:'', audience:'teacher', targetType:'role', targetUserId:'' });

  useEffect(() => {
    api.get('/coordinator/notices').then(r => setNotices(r.data)).catch(() => {}).finally(() => setLoading(false));
    // Mark as read
    api.post('/coordinator/notices/mark-read').catch(() => {});
    api.get('/coordinator/teachers').then(r => setTeachers(r.data?.teachers || [])).catch(() => {});
    api.get('/coordinator/coordinators').then(r => setCoordinators(r.data || [])).catch(() => {});
    api.get('/coordinator/finance-members').then(r => setFinanceMembers(r.data || [])).catch(() => {});
    api.get('/coordinator/classes').then(r => setClasses(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.audience !== 'student') return;
    setForm(f => ({ ...f, targetUserId:'' }));
    if (!filterClass) { setStudents([]); return; }
    const params = new URLSearchParams({ classId: filterClass });
    if (filterSection) params.set('section', filterSection);
    api.get(`/coordinator/students?${params}`).then(r => setStudents(r.data?.students || [])).catch(() => setStudents([]));
  }, [form.audience, filterClass, filterSection]);

  const peopleFor = (audience) => audience === 'teacher' ? teachers
    : audience === 'coordinator' ? coordinators
    : audience === 'finance' ? financeMembers
    : students;
  const personLabel = (p) => `${p.user?.name || p.name} — ${p.user?.email || p.email || ''}`;

  const handleSubmit = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    if (form.targetType === 'specific' && !form.targetUserId) { toast.error('Select a person'); return; }
    try {
      setSaving(true);
      const targetPerson = form.targetType === 'specific'
        ? peopleFor(form.audience).find(p => String(p.user?._id || p._id) === String(form.targetUserId))
        : null;
      const payload = {
        title:   form.title,
        content: form.content,
        audience: [form.audience],
        targetUsers: form.targetType === 'specific' && form.targetUserId ? [form.targetUserId] : [],
        targetUserName: targetPerson ? personLabel(targetPerson) : undefined,
      };
      await api.post('/coordinator/requests', { type: 'notice_post', payload });
      toast.success('Notice request submitted — awaiting admin approval');
      setOpen(false);
      setForm({ title:'', content:'', audience:'teacher', targetType:'role', targetUserId:'' });
      setFilterClass(''); setFilterSection('');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header">
        <div><div className="cp-title">Notices</div><div className="cp-sub">View notices — post requests go to admin for approval</div></div>
        <button className="btn btn-orange" onClick={() => setOpen(true)}><PlusIcon />Post Notice</button>
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">All Notices ({notices.length})</div></div>
        {loading ? <div className="cp-loading"><div className="spinner"/>Loading…</div>
        : notices.length === 0 ? <div className="cp-empty">No notices yet.</div>
        : notices.map(n => (
          <div key={n._id} style={{padding:'14px 18px',borderBottom:'1px solid #f5f6fb'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,flexWrap:'wrap',marginBottom:6}}>
              <div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{n.title}</div>
              <span style={{fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:'#eef2ff',color:'#3b6ef8'}}>{n.audience?.join(', ')}</span>
            </div>
            <div style={{fontSize:'.8rem',color:'#4a5075',lineHeight:1.6,marginBottom:6}}>{n.content}</div>
            <div style={{fontSize:'.7rem',color:'#b0b5cc'}}>{n.createdBy?.name} · {fmtDate(n.createdAt)}</div>
          </div>
        ))}
      </div>

      {open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Post Notice (Request)</div>
              <button className="modal-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div className="info-banner">📋 This will be sent to admin for approval before being published.</div>
              <div className="form-grid">
                <div className="form-field full"><label className="form-label">Title <span className="req">*</span></label>
                  <input type="text" className="form-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Notice title…" /></div>
                <div className="form-field full"><label className="form-label">Content <span className="req">*</span></label>
                  <textarea className="form-textarea" value={form.content} onChange={e=>setForm({...form,content:e.target.value})} placeholder="Write your notice…" /></div>
                <div className="form-field"><label className="form-label">Audience</label>
                  <select className="form-select" value={form.audience} onChange={e=>setForm({...form,audience:e.target.value,targetUserId:''})}>
                    {COORDINATOR_NOTICE_AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select></div>
                <div className="form-field"><label className="form-label">Target</label>
                  <select className="form-select" value={form.targetType} onChange={e=>setForm({...form,targetType:e.target.value,targetUserId:''})} disabled={form.audience==='all'}>
                    <option value="role">All in role</option>
                    <option value="specific">Specific person</option>
                  </select></div>
                {form.targetType === 'specific' && form.audience === 'student' && (
                  <>
                    <div className="form-field"><label className="form-label">Class</label>
                      <select className="form-select" value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSection('');}}>
                        <option value="">Select class…</option>
                        {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </select></div>
                    <div className="form-field"><label className="form-label">Section</label>
                      <select className="form-select" value={filterSection} onChange={e=>setFilterSection(e.target.value)} disabled={!filterClass}>
                        <option value="">All sections</option>
                        {(classes.find(c=>String(c._id)===String(filterClass))?.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
                      </select></div>
                  </>
                )}
                {form.targetType === 'specific' && (
                  <div className="form-field full"><label className="form-label">Select Person</label>
                    <select className="form-select" value={form.targetUserId} onChange={e=>setForm({...form,targetUserId:e.target.value})}
                      disabled={form.audience==='student' && !filterClass}>
                      <option value="">{form.audience==='student' && !filterClass ? 'Select a class first' : 'Select…'}</option>
                      {peopleFor(form.audience).map(p => (
                        <option key={p.user?._id || p._id} value={p.user?._id || p._id}>{personLabel(p)}</option>
                      ))}
                    </select></div>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleSubmit} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

// ─── MY REQUESTS ─────────────────────────────────────────────────────────────
export const CoordinatorRequests = () => {
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [open,      setOpen]      = useState(false);
  const [reqType,   setReqType]   = useState('student_add');
  const [saving,    setSaving]    = useState(false);
  const [classes,   setClasses]   = useState([]);
  const [form,      setForm]      = useState({});

  useEffect(() => {
    api.get('/coordinator/requests').then(r => setRequests(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/coordinator/classes').then(r => setClasses(r.data)).catch(() => {});
  }, []);

  const CREATABLE_TYPE_LABELS = { student_add:'Add Student', teacher_add:'Add Teacher', class_add:'Add Class', notice_post:'Post Notice', material_upload:'Upload Material' };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await api.post('/coordinator/requests', { type: reqType, payload: form });
      toast.success('Request submitted — awaiting admin approval');
      setOpen(false); setForm({});
      const r = await api.get('/coordinator/requests');
      setRequests(r.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getSections = (classId) => classes.find(c => String(c._id) === classId)?.sections || [];

  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header">
        <div><div className="cp-title">My Requests</div><div className="cp-sub">Submit requests to admin for approval</div></div>
        <button className="btn btn-orange" onClick={() => { setOpen(true); setForm({}); }}><PlusIcon />New Request</button>
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Requests ({requests.length})</div></div>
        {loading ? <div className="cp-loading"><div className="spinner"/>Loading…</div>
        : requests.length === 0 ? <div className="cp-empty">No requests yet. Click "New Request" to submit one.</div>
        : requests.map(r => (
          <div key={r._id} className="row-item">
            <div style={{flex:1,minWidth:0}}>
              <div className="row-title">{REQUEST_TYPE_LABELS[r.type] || r.type}</div>
              <div className="row-sub">
                {payloadSummary(r.type, r.payload)} · {fmtDateDMY(r.createdAt)}
              </div>
              {r.reviewNote && <div style={{fontSize:'.7rem',color:'#6b7194',marginTop:3,fontStyle:'italic'}}>Note: {r.reviewNote}</div>}
            </div>
            <span className={`pill pill-${r.status}`}>{r.status}</span>
          </div>
        ))}
      </div>

      {open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">New Request</div>
              <button className="modal-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div className="info-banner">📋 All requests require admin approval before taking effect.</div>
              <div className="form-field" style={{marginBottom:14}}>
                <label className="form-label">Request Type</label>
                <select className="form-select" value={reqType} onChange={e => { setReqType(e.target.value); setForm({}); }}>
                  {Object.entries(CREATABLE_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              {/* Student Add Form */}
              {reqType === 'student_add' && (
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Full Name <span className="req">*</span></label><input type="text" className="form-input" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Email <span className="req">*</span></label><input type="email" className="form-input" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Admission No</label><input type="text" className="form-input" value={form.admissionNo||''} onChange={e=>setForm({...form,admissionNo:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Class</label>
                    <select className="form-select" value={form.classId||''} onChange={e=>setForm({...form,classId:e.target.value,section:''})}>
                      <option value="">Select class…</option>{classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                    </select></div>
                  <div className="form-field"><label className="form-label">Section</label>
                    <select className="form-select" value={form.section||''} onChange={e=>setForm({...form,section:e.target.value})} disabled={!form.classId}>
                      <option value="">Select…</option>{getSections(form.classId).map(s=><option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div className="form-field"><label className="form-label">Roll No</label><input type="text" className="form-input" value={form.rollNo||''} onChange={e=>setForm({...form,rollNo:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Parent Name</label><input type="text" className="form-input" value={form.parentName||''} onChange={e=>setForm({...form,parentName:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Parent Contact</label><input type="text" className="form-input" value={form.parentContact||''} onChange={e=>setForm({...form,parentContact:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Parent/Guardian CNIC</label><input type="text" className="form-input" placeholder="12345-1234567-1" maxLength={15} value={form.parentCnic||''} onChange={e=>setForm({...form,parentCnic:e.target.value})} /></div>
                </div>
              )}

              {/* Teacher Add Form */}
              {reqType === 'teacher_add' && (
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Full Name <span className="req">*</span></label><input type="text" className="form-input" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Email <span className="req">*</span></label><input type="email" className="form-input" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Employee ID</label><input type="text" className="form-input" value={form.employeeId||''} onChange={e=>setForm({...form,employeeId:e.target.value})} /></div>
                  <div className="form-field full"><label className="form-label">Subjects (comma separated)</label><input type="text" className="form-input" value={form.subjectsStr||''} onChange={e=>setForm({...form,subjectsStr:e.target.value,subjects:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} placeholder="Math, Science, English" /></div>
                </div>
              )}

              {/* Class Add Form */}
              {reqType === 'class_add' && (
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Class Name <span className="req">*</span></label><input type="text" className="form-input" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Grade 9" /></div>
                  <div className="form-field"><label className="form-label">Sections (comma separated)</label><input type="text" className="form-input" value={form.sectionsStr||''} onChange={e=>setForm({...form,sectionsStr:e.target.value,sections:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} placeholder="A, B, C" /></div>
                  <div className="form-field"><label className="form-label">Subjects (comma separated)</label><input type="text" className="form-input" value={form.subjectsStr||''} onChange={e=>setForm({...form,subjectsStr:e.target.value,subjects:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} placeholder="Math, Science" /></div>
                </div>
              )}

              {/* Notice Post Form */}
              {reqType === 'notice_post' && (
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Title <span className="req">*</span></label><input type="text" className="form-input" value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} /></div>
                  <div className="form-field full"><label className="form-label">Content <span className="req">*</span></label><textarea className="form-textarea" value={form.content||''} onChange={e=>setForm({...form,content:e.target.value})} /></div>
                  <div className="form-field full"><label className="form-label">Audience</label>
                    <select className="form-select" value={form.audience||'teacher'} onChange={e=>setForm({...form,audience:e.target.value})}>
                      <option value="teacher">Teachers</option><option value="student">Students</option><option value="all">All</option>
                    </select></div>
                </div>
              )}

              {/* Material Upload Form */}
              {reqType === 'material_upload' && (
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Title <span className="req">*</span></label><input type="text" className="form-input" value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} /></div>
                  <div className="form-field full"><label className="form-label">File URL (paste Cloudinary/external URL)</label><input type="url" className="form-input" value={form.fileUrl||''} onChange={e=>setForm({...form,fileUrl:e.target.value})} placeholder="https://…" /></div>
                  <div className="form-field"><label className="form-label">Type</label>
                    <select className="form-select" value={form.type||'pdf'} onChange={e=>setForm({...form,type:e.target.value})}>
                      {['pdf','doc','ppt','image','video','youtube','other'].map(t=><option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select></div>
                  <div className="form-field"><label className="form-label">Subject</label><input type="text" className="form-input" value={form.subject||''} onChange={e=>setForm({...form,subject:e.target.value})} /></div>
                  <div className="form-field"><label className="form-label">Class</label>
                    <select className="form-select" value={form.classId||''} onChange={e=>setForm({...form,classId:e.target.value})}>
                      <option value="">All Classes</option>{classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                    </select></div>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleSubmit} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

// ─── VIEW-ONLY: Fees, Attendance, Salary ──────────────────────────────────────
export const CoordinatorFees = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('');
  useEffect(() => {
    setLoading(true);
    const q = month ? `?month=${month}` : '';
    api.get(`/coordinator/fees${q}`)
      .then(r => setFees(Array.isArray(r.data) ? r.data : []))
      .catch(err => { console.error('Fees error:', err); toast.error('Failed to load fees'); })
      .finally(() => setLoading(false));
  }, [month]);
  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header"><div><div className="cp-title">Fee Records</div><div className="cp-sub">View only</div></div></div>
      <div style={{marginBottom:'1rem'}}><input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}} /></div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Fee Records ({fees.length})</div></div>
        {loading?<div className="cp-loading"><div className="spinner"/>Loading…</div>
        :fees.length===0?<div className="cp-empty">No records.</div>
        :fees.map(f=>(
          <div key={f._id} className="row-item">
            <div style={{flex:2,minWidth:0}}><div className="row-title">{f.student?.user?.name}</div><div className="row-sub">{f.student?.admissionNo} · {f.month}</div></div>
            <div style={{textAlign:'right'}}><div style={{fontWeight:700,fontFamily:"'JetBrains Mono',monospace",fontSize:'.84rem'}}>Rs. {(f.totalDue||0).toLocaleString()}</div><span className={`pill pill-${f.status}`}>{f.status}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CoordinatorTeacherAttendance = () => {
  const [teachers,   setTeachers]   = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [date,       setDate]       = useState(new Date().toISOString().slice(0,10));
  const [filterMode, setFilterMode] = useState('date');
  const [month,      setMonth]      = useState(new Date().toISOString().slice(0,7));
  const [monthRecs,  setMonthRecs]  = useState([]);
  const [editModal,  setEditModal]  = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [addModal,   setAddModal]   = useState(null);
  const [addForm,    setAddForm]    = useState({ checkIn:'09:00', checkOut:'', status:'present', lateMinutes:'0' });
  const [saving,     setSaving]     = useState(false);

  const todayStr = new Date().toISOString().slice(0,10);

  useEffect(() => {
    if (filterMode === 'date') fetchDaily();
    else fetchMonthly();
  }, [date, month, filterMode]);

  const fetchDaily = async () => {
    try {
      setLoading(true);
      const [tRes, aRes] = await Promise.all([
        api.get('/coordinator/teachers'),
        api.get(`/coordinator/teacher-attendance?date=${date}`),
      ]);
      setTeachers(Array.isArray(tRes.data?.teachers) ? tRes.data.teachers : []);
      const map = {};
      (Array.isArray(aRes.data) ? aRes.data : []).forEach(r => { if (r.user?._id) map[String(r.user._id)] = r; });
      setAttendance(map);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchMonthly = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/coordinator/teacher-attendance?month=${month}`);
      setMonthRecs(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';
  const ini = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

  const submitRequest = async (type, payload) => {
    try {
      setSaving(true);
      await api.post('/coordinator/requests', { type: 'attendance_action', payload: { action: type, ...payload } });
      toast.success('Request submitted — awaiting admin approval');
      setEditModal(null); setAddModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getStatus = (userId) => {
    const r = attendance[userId];
    if (!r) return { label:'Not marked', cls:'absent' };
    if (r.checkIn && !r.checkOut) return { label:'Checked In', cls:'checkedin' };
    if (r.checkIn && r.checkOut)  return { label:'Present',    cls:'present' };
    if (r.status === 'on-leave')  return { label:'On Leave',   cls:'on-leave' };
    return { label: r.status || 'Absent', cls: r.status || 'absent' };
  };

  const presentCount   = teachers.filter(t => { const r=attendance[String(t.user?._id)]; return r?.checkIn && r?.checkOut; }).length;
  const checkedInCount = teachers.filter(t => { const r=attendance[String(t.user?._id)]; return r?.checkIn && !r?.checkOut; }).length;
  const absentCount    = teachers.filter(t => !attendance[String(t.user?._id)]).length;

  const sharedInputStyle = { padding:'8px 12px', border:'1.5px solid #e0e4f0', borderRadius:10, fontFamily:"'Sora',sans-serif", fontSize:'.82rem', background:'#f8f9fc', outline:'none' };

  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header">
        <div><div className="cp-title">Teacher Attendance</div><div className="cp-sub">View · submit requests to edit or add records</div></div>
      </div>
      <div className="req-banner" style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#c2410c',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:8}}>
        📋 Edit and Add actions require admin approval.
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:'1rem',alignItems:'center'}}>
        <select value={filterMode} onChange={e=>setFilterMode(e.target.value)} style={sharedInputStyle}>
          <option value="date">By Date</option>
          <option value="month">By Month</option>
        </select>
        {filterMode==='date'
          ? <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={sharedInputStyle} />
          : <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={sharedInputStyle} />
        }
        {filterMode==='date' && date !== todayStr && <button className="btn btn-ghost" style={{padding:'8px 12px',fontSize:'.78rem'}} onClick={() => setDate(todayStr)}>Today</button>}
      </div>

      {filterMode==='date' && (
        <div style={{display:'flex',gap:8,marginBottom:'1rem',flexWrap:'wrap'}}>
          {[{label:`Total: ${teachers.length}`,bg:'#f0f2f7',color:'#6b7194'},{label:`Present: ${presentCount}`,bg:'#ecfdf5',color:'#10c98e'},{label:`In: ${checkedInCount}`,bg:'#eef2ff',color:'#3b6ef8'},{label:`Not Marked: ${absentCount}`,bg:'#fef2f2',color:'#ef4444'}].map(s=>(
            <span key={s.label} style={{padding:'6px 14px',borderRadius:99,fontSize:'.76rem',fontWeight:600,background:s.bg,color:s.color,whiteSpace:'nowrap'}}>{s.label}</span>
          ))}
        </div>
      )}

      <div className="panel">
        <div className="panel-head"><div className="panel-title">{filterMode==='date' ? (date===todayStr?"Today's Attendance":`Attendance — ${new Date(date).toLocaleDateString('en-US',{weekday:'long',day:'numeric',month:'long'})}`) : `Records — ${month}`}</div></div>
        {loading ? <div className="cp-loading"><div className="spinner"/>Loading…</div>
        : filterMode==='date' ? (
          teachers.length===0 ? <div className="cp-empty">No teachers found.</div>
          : <>
          <div className="cp-tbl-wrap" style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.8rem'}}>
              <thead style={{background:'#f8f9fc',borderBottom:'1px solid #eaecf4'}}>
                <tr>{['Teacher','Status','Check In','Check Out','Work Hrs','Late Min','Actions'].map(h=>(
                  <th key={h} style={{padding:'10px 14px',textAlign:'center',fontSize:'.65rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.07em',whiteSpace:'nowrap'}}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>{teachers.map(t => {
                const userId = String(t.user?._id);
                const rec = attendance[userId];
                const { label, cls } = getStatus(userId);
                const statusColors = { present:'#ecfdf5', absent:'#fef2f2', checkedin:'#eef2ff', 'on-leave':'#f3f0ff' };
                const statusTextColors = { present:'#10c98e', absent:'#ef4444', checkedin:'#3b6ef8', 'on-leave':'#8b5cf6' };
                return (
                  <tr key={t._id} style={{borderBottom:'1px solid #f3f5fb'}}>
                    <td style={{textAlign:'center',padding:'11px 14px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div style={{width:32,height:32,borderRadius:9,background:'#ecfdf5',color:'#10c98e',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.72rem',fontWeight:700,flexShrink:0}}>{ini(t.user?.name)}</div>
                        <div><div style={{fontWeight:600,fontSize:'.82rem'}}>{t.user?.name}</div><div style={{fontSize:'.7rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>{t.employeeId||'—'}</div></div>
                      </div>
                    </td>
                    <td style={{textAlign:'center',padding:'11px 14px'}}><span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'.72rem',fontWeight:600,padding:'4px 10px',borderRadius:99,background:statusColors[cls]||'#f0f2f7',color:statusTextColors[cls]||'#6b7194',whiteSpace:'nowrap'}}>{label}</span></td>
                    <td style={{textAlign:'center',padding:'11px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#10c98e'}}>{fmtTime(rec?.checkIn)}</td>
                    <td style={{textAlign:'center',padding:'11px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#3b6ef8'}}>{fmtTime(rec?.checkOut)}</td>
                    <td style={{textAlign:'center',padding:'11px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600}}>{rec?.workHours ? Number(rec.workHours).toFixed(2)+'h' : '—'}</td>
                    <td style={{textAlign:'center',padding:'11px 14px',fontSize:'.78rem',color:rec?.lateMinutes>0?'#f59e0b':'#9094a8'}}>{rec?.lateMinutes>0?`${rec.lateMinutes}m`:'—'}</td>
                    <td style={{textAlign:'center',padding:'11px 14px'}}>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {rec && <button className="btn btn-ghost" style={{padding:'5px 10px',fontSize:'.72rem'}} onClick={() => { setEditModal({rec,teacher:t}); setEditForm({status:rec.status||'present',checkIn:rec.checkIn?new Date(rec.checkIn).toTimeString().slice(0,5):'',checkOut:rec.checkOut?new Date(rec.checkOut).toTimeString().slice(0,5):'',lateMinutes:String(rec.lateMinutes||0),earlyExitMinutes:String(rec.earlyExitMinutes||0)}); }}>✏ Edit</button>}
                        {!rec && <button className="btn btn-ghost" style={{padding:'5px 10px',fontSize:'.72rem'}} onClick={() => { setAddModal({teacher:t}); setAddForm({checkIn:'09:00',checkOut:'',status:'present',lateMinutes:'0',earlyExitMinutes:'0'}); }}>+ Add</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          <div className="cp-cards">
            {teachers.map(t => {
              const userId = String(t.user?._id);
              const rec = attendance[userId];
              const { label, cls } = getStatus(userId);
              const statusColors = { present:'#ecfdf5', absent:'#fef2f2', checkedin:'#eef2ff', 'on-leave':'#f3f0ff' };
              const statusTextColors = { present:'#10c98e', absent:'#ef4444', checkedin:'#3b6ef8', 'on-leave':'#8b5cf6' };
              return (
                <div key={t._id} className="cp-card">
                  <div className="cp-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:9,minWidth:0}}>
                      <div style={{width:32,height:32,borderRadius:9,background:'#ecfdf5',color:'#10c98e',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.72rem',fontWeight:700,flexShrink:0}}>{ini(t.user?.name)}</div>
                      <div style={{minWidth:0}}><div style={{fontWeight:600,fontSize:'.82rem'}}>{t.user?.name}</div><div style={{fontSize:'.7rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>{t.employeeId||'—'}</div></div>
                    </div>
                    <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'.72rem',fontWeight:600,padding:'4px 10px',borderRadius:99,background:statusColors[cls]||'#f0f2f7',color:statusTextColors[cls]||'#6b7194',whiteSpace:'nowrap',flexShrink:0}}>{label}</span>
                  </div>
                  <div className="cp-card-row"><span className="cp-card-label">Check In</span><span style={{fontFamily:"'JetBrains Mono',monospace",color:'#10c98e'}}>{fmtTime(rec?.checkIn)}</span></div>
                  <div className="cp-card-row"><span className="cp-card-label">Check Out</span><span style={{fontFamily:"'JetBrains Mono',monospace",color:'#3b6ef8'}}>{fmtTime(rec?.checkOut)}</span></div>
                  <div className="cp-card-row"><span className="cp-card-label">Work Hrs</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>{rec?.workHours ? Number(rec.workHours).toFixed(2)+'h' : '—'}</span></div>
                  <div className="cp-card-row"><span className="cp-card-label">Late Min</span><span style={{color:rec?.lateMinutes>0?'#f59e0b':'#9094a8'}}>{rec?.lateMinutes>0?`${rec.lateMinutes}m`:'—'}</span></div>
                  <div className="cp-card-actions">
                    {rec && <button className="btn btn-ghost btn-sm" onClick={() => { setEditModal({rec,teacher:t}); setEditForm({status:rec.status||'present',checkIn:rec.checkIn?new Date(rec.checkIn).toTimeString().slice(0,5):'',checkOut:rec.checkOut?new Date(rec.checkOut).toTimeString().slice(0,5):'',lateMinutes:String(rec.lateMinutes||0),earlyExitMinutes:String(rec.earlyExitMinutes||0)}); }}>✏ Edit</button>}
                    {!rec && <button className="btn btn-ghost btn-sm" onClick={() => { setAddModal({teacher:t}); setAddForm({checkIn:'09:00',checkOut:'',status:'present',lateMinutes:'0',earlyExitMinutes:'0'}); }}>+ Add</button>}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        ) : (
          monthRecs.length===0 ? <div className="cp-empty">No records for {month}.</div>
          : <>
          <div className="cp-tbl-wrap" style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.8rem'}}>
              <thead style={{background:'#f8f9fc',borderBottom:'1px solid #eaecf4'}}>
                <tr>{['Teacher','Date','Check In','Check Out','Hours','Late','Status'].map(h=>(
                  <th key={h} style={{padding:'9px 14px',textAlign:'center',fontSize:'.65rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.07em',whiteSpace:'nowrap'}}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>{monthRecs.map(r => (
                <tr key={r._id} style={{borderBottom:'1px solid #f3f5fb'}}>
                  <td style={{textAlign:'center',padding:'10px 14px'}}><div style={{fontWeight:600,fontSize:'.82rem'}}>{r.user?.name}</div><div style={{fontSize:'.7rem',color:'#9094a8'}}>{r.employeeId||r.user?.email}</div></td>
                  <td style={{textAlign:'center',padding:'10px 14px',fontSize:'.78rem',color:'#6b7194'}}>{r.date?new Date(r.date).toLocaleDateString('en-PK',{day:'numeric',month:'short'}):'—'}</td>
                  <td style={{textAlign:'center',padding:'10px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#10c98e'}}>{fmtTime(r.checkIn)}</td>
                  <td style={{textAlign:'center',padding:'10px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#3b6ef8'}}>{fmtTime(r.checkOut)}</td>
                  <td style={{textAlign:'center',padding:'10px 14px',fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600}}>{r.workHours?Number(r.workHours).toFixed(2)+'h':'—'}</td>
                  <td style={{textAlign:'center',padding:'10px 14px',fontSize:'.78rem',color:r.lateMinutes>0?'#f59e0b':'#9094a8'}}>{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</td>
                  <td style={{textAlign:'center',padding:'10px 14px'}}><span style={{display:'inline-flex',fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:r.status==='present'?'#ecfdf5':'#fef2f2',color:r.status==='present'?'#10c98e':'#ef4444'}}>{r.status||'present'}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="cp-cards">
            {monthRecs.map(r => (
              <div key={r._id} className="cp-card">
                <div className="cp-card-top">
                  <div><div style={{fontWeight:600,fontSize:'.82rem'}}>{r.user?.name}</div><div style={{fontSize:'.7rem',color:'#9094a8'}}>{r.employeeId||r.user?.email}</div></div>
                  <span style={{display:'inline-flex',fontSize:'.67rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:r.status==='present'?'#ecfdf5':'#fef2f2',color:r.status==='present'?'#10c98e':'#ef4444',flexShrink:0}}>{r.status||'present'}</span>
                </div>
                <div className="cp-card-row"><span className="cp-card-label">Date</span><span style={{color:'#6b7194'}}>{r.date?new Date(r.date).toLocaleDateString('en-PK',{day:'numeric',month:'short'}):'—'}</span></div>
                <div className="cp-card-row"><span className="cp-card-label">Check In</span><span style={{fontFamily:"'JetBrains Mono',monospace",color:'#10c98e'}}>{fmtTime(r.checkIn)}</span></div>
                <div className="cp-card-row"><span className="cp-card-label">Check Out</span><span style={{fontFamily:"'JetBrains Mono',monospace",color:'#3b6ef8'}}>{fmtTime(r.checkOut)}</span></div>
                <div className="cp-card-row"><span className="cp-card-label">Hours</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>{r.workHours?Number(r.workHours).toFixed(2)+'h':'—'}</span></div>
                <div className="cp-card-row"><span className="cp-card-label">Late</span><span style={{color:r.lateMinutes>0?'#f59e0b':'#9094a8'}}>{r.lateMinutes>0?`${r.lateMinutes}m`:'—'}</span></div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.55)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}} onClick={() => setEditModal(null)}>
          <div style={{background:'white',borderRadius:20,padding:26,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}><div style={{fontWeight:700,fontSize:'.95rem',color:'#0f1123'}}>Request: Edit Attendance</div><button onClick={()=>setEditModal(null)} style={{width:28,height:28,borderRadius:7,border:'none',background:'#f0f2f7',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button></div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:14}}>{editModal.teacher?.user?.name} · {date}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[['Check In','checkIn','time'],['Check Out','checkOut','time'],['Late Min','lateMinutes','number'],['Early Exit','earlyExitMinutes','number']].map(([lbl,key,type])=>(
                <div key={key} style={{display:'flex',flexDirection:'column',gap:5}}>
                  <label style={{fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>{lbl}</label>
                  <input type={type} style={sharedInputStyle} value={editForm[key]||''} min={type==='number'?0:undefined} onChange={e=>setEditForm({...editForm,[key]:e.target.value})} />
                </div>
              ))}
              <div style={{gridColumn:'1/-1',display:'flex',flexDirection:'column',gap:5}}>
                <label style={{fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>Status</label>
                <select style={sharedInputStyle} value={editForm.status||'present'} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option><option value="on-leave">On Leave</option>
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setEditModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={()=>submitRequest('edit_attendance',{recordId:editModal.rec._id,teacherName:editModal.teacher?.user?.name,date,...editForm})} disabled={saving} style={{background:'#f97316',color:'white',border:'none',borderRadius:10,padding:'9px 16px',fontFamily:"'Sora',sans-serif",fontSize:'.8rem',fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:7}}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {addModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.55)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}} onClick={() => setAddModal(null)}>
          <div style={{background:'white',borderRadius:20,padding:26,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}><div style={{fontWeight:700,fontSize:'.95rem',color:'#0f1123'}}>Request: Add Attendance</div><button onClick={()=>setAddModal(null)} style={{width:28,height:28,borderRadius:7,border:'none',background:'#f0f2f7',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button></div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:14}}>{addModal.teacher?.user?.name} · {date}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[['Check In','checkIn','time'],['Check Out','checkOut','time'],['Late Min','lateMinutes','number']].map(([lbl,key,type])=>(
                <div key={key} style={{display:'flex',flexDirection:'column',gap:5}}>
                  <label style={{fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>{lbl}</label>
                  <input type={type} style={sharedInputStyle} value={addForm[key]||''} min={type==='number'?0:undefined} onChange={e=>setAddForm({...addForm,[key]:e.target.value})} />
                </div>
              ))}
              <div style={{display:'flex',flexDirection:'column',gap:5}}>
                <label style={{fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>Status</label>
                <select style={sharedInputStyle} value={addForm.status||'present'} onChange={e=>setAddForm({...addForm,status:e.target.value})}>
                  <option value="present">Present</option><option value="absent">Absent</option>
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setAddModal(null)}>Cancel</button>
              <button onClick={()=>submitRequest('add_attendance',{teacherId:addModal.teacher?._id,teacherName:addModal.teacher?.user?.name,date,...addForm})} disabled={saving} style={{background:'#f97316',color:'white',border:'none',borderRadius:10,padding:'9px 16px',fontFamily:"'Sora',sans-serif",fontSize:'.8rem',fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:7}}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export const CoordinatorStudentAttendance = () => {
  const [records, setRecords] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState('');
  const [section, setSection] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  useEffect(() => { api.get('/coordinator/classes').then(r=>setClasses(r.data)).catch(()=>{}); }, []);
  const [filterMode, setFilterMode] = useState('date');
  const [month, setMonth] = useState(new Date().toISOString().slice(0,7));
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filterMode === 'date') q.set('date', date);
    else q.set('month', month);
    if (classId) q.set('classId', classId);
    if (section) q.set('section', section);
    api.get(`/coordinator/student-attendance?${q}`)
      .then(r => setRecords(Array.isArray(r.data) ? r.data : []))
      .catch(err => { console.error('StudentAtt error:', err); toast.error('Failed to load'); })
      .finally(() => setLoading(false));
  }, [date, month, filterMode, classId, section]);
  const sections = classId ? (classes.find(c=>String(c._id)===classId)?.sections||[]) : [];
  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header"><div><div className="cp-title">Student Attendance</div><div className="cp-sub">View only</div></div></div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:'1rem',alignItems:'center'}}>
        <select value={filterMode} onChange={e=>setFilterMode(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}}>
          <option value="date">By Date</option>
          <option value="month">By Month</option>
        </select>
        {filterMode==='date'
          ? <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}} />
          : <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}} />
        }
        <select value={classId} onChange={e=>{setClassId(e.target.value);setSection('');}} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}}>
          <option value="">All Classes</option>{sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        {sections.length>0&&<select value={section} onChange={e=>setSection(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}}>
          <option value="">All Sections</option>{sortAsc(sections).map(s=><option key={s} value={s}>Section {s}</option>)}
        </select>}
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Records ({records.length})</div></div>
        {loading?<div className="cp-loading"><div className="spinner"/>Loading…</div>
        :records.length===0?<div className="cp-empty">No records.</div>
        :records.map(r=>(
          <div key={r._id} className="row-item">
            <div style={{flex:1}}><div className="row-title">{r.student?.user?.name}</div><div className="row-sub">{r.student?.rollNo} · {r.student?.admissionNo}</div></div>
            <span className={`pill ${r.status==='present'?'pill-approved':r.status==='absent'?'pill-rejected':r.status==='on-leave'?'pill-orange':'pill-pending'}`}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CoordinatorSalary = () => {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('');
  useEffect(() => {
    setLoading(true);
    const q = month ? `?month=${month}` : '';
    api.get(`/coordinator/salary${q}`)
      .then(r => setSalaries(Array.isArray(r.data) ? r.data : []))
      .catch(err => { console.error('Salary error:', err); toast.error('Failed to load salary'); })
      .finally(() => setLoading(false));
  }, [month]);
  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header"><div><div className="cp-title">Salary Records</div><div className="cp-sub">View only</div></div></div>
      <div style={{marginBottom:'1rem'}}><input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none'}} /></div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Salary Records ({salaries.length})</div></div>
        {loading?<div className="cp-loading"><div className="spinner"/>Loading…</div>
        :salaries.length===0?<div className="cp-empty">No records.</div>
        :salaries.map(s=>(
          <div key={s._id} className="row-item">
            <div style={{flex:1}}><div className="row-title">{s.teacher?.user?.name}</div><div className="row-sub">{s.month}</div></div>
            <div style={{textAlign:'right'}}><div style={{fontWeight:700,fontFamily:"'JetBrains Mono',monospace",fontSize:'.84rem'}}>Rs. {(s.netSalary||s.amount||0).toLocaleString()}</div><span className={`pill ${s.status==='paid'?'pill-approved':'pill-pending'}`}>{s.status||'generated'}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CoordinatorProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:'' });
  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
  useEffect(() => {
    api.get('/coordinator/profile').then(r=>{ setProfile(r.data); setForm({ name: r.data.name||'' }); }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  const save = async () => { try{ setSaving(true); await api.put('/coordinator/profile',form); toast.success('Updated'); }catch{ toast.error('Failed'); }finally{ setSaving(false); } };
  const changePw = async () => {
    if (pwForm.newPassword !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    try{ await api.put('/coordinator/profile/password',pwForm); toast.success('Password changed'); setPwForm({currentPassword:'',newPassword:'',confirm:''}); }
    catch(e){ toast.error(e.response?.data?.message||'Failed'); }
  };
  if (loading) return <div className="cp"><style>{sharedStyle}</style><div className="cp-loading"><div className="spinner"/>Loading…</div></div>;
  const ini = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
  return (
    <div className="cp"><style>{sharedStyle}</style>
      <div className="cp-header"><div><div className="cp-title">My Profile</div></div></div>
      <div style={{background:'linear-gradient(135deg,#f97316,#ef4444)',borderRadius:16,padding:'1.4rem 1.6rem',color:'white',marginBottom:'1rem',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{width:56,height:56,borderRadius:14,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',fontWeight:700,flexShrink:0,fontFamily:"'Sora',sans-serif"}}>{ini(profile?.name)}</div>
        <div><div style={{fontSize:'1.1rem',fontWeight:700,fontFamily:"'Sora',sans-serif"}}>{profile?.name}</div><div style={{fontSize:'.76rem',opacity:.8,fontFamily:"'Sora',sans-serif"}}>{profile?.email}</div>
        <div style={{fontSize:'.72rem',opacity:.7,marginTop:4,fontFamily:"'Sora',sans-serif"}}>Employee ID: {profile?.employeeId} · Mobile: {profile?.mobile} · CNIC: {profile?.cnic}</div></div>
      </div>
      <div className="panel" style={{marginBottom:'1rem'}}>
        <div className="panel-head"><div className="panel-title">Update Name</div></div>
        <div style={{padding:'16px 18px'}}>
          <input type="text" value={form.name} onChange={e=>setForm({name:e.target.value})} style={{padding:'9px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none',width:'100%',maxWidth:360,marginBottom:12}} />
          <button className="btn btn-orange" onClick={save} disabled={saving}>{saving?'Saving…':'Update'}</button>
        </div>
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Change Password</div></div>
        <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:10,maxWidth:400}}>
          {[['Current Password','currentPassword'],['New Password','newPassword'],['Confirm','confirm']].map(([lbl,key])=>(
            <div key={key}><label style={{display:'block',fontSize:'.68rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',marginBottom:4,fontFamily:"'Sora',sans-serif"}}>{lbl}</label>
            <PasswordInput value={pwForm[key]} onChange={e=>setPwForm({...pwForm,[key]:e.target.value})} style={{padding:'9px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:"'Sora',sans-serif",fontSize:'.82rem',background:'#f8f9fc',outline:'none',width:'100%'}} /></div>
          ))}
          <button className="btn btn-orange" style={{alignSelf:'flex-start'}} onClick={changePw}>Change Password</button>
        </div>
      </div>
    </div>
  );
};